import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Heading, Text, VStack, HStack, SimpleGrid, Badge, Icon, Card, Flex, Button,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon
} from "@chakra-ui/react";
import { FiCalendar, FiMapPin, FiUser } from "react-icons/fi";
import { FaCalendarPlus } from "react-icons/fa";
import moment from "moment";
import { getCertStatus, getModuleStatus, getOrdinalSuffix } from "../utils/formatHelpers";
import { generateMultipleICSContent, downloadICS } from "../utils/calendarHelpers";

export const CoursesTab = ({ certifications, modules, standaloneCourses = [], isMobile, toast }) => {

    const handleDownloadAllCertificationCalendar = (certId, courseTitle) => {
        const allSessions = [];
        if (modules[certId] && modules[certId].length > 0) {
            modules[certId].forEach(module => {
                const moduleName = module.course_type?.name || module.course_type_code || "Module";
                if (module.course_dates && module.course_dates.length > 0) {
                    module.course_dates.forEach(cd => {
                        allSessions.push({
                            date: moment(cd.date).format("YYYY-MM-DD"),
                            start_time: cd.start_time, end_time: cd.end_time,
                            venue: cd.venue, venueAddress: cd.venueAddress, room: cd.room,
                            session_id: cd.date, moduleName,
                        });
                    });
                }
            });
        }
        if (allSessions.length === 0) {
            toast({ title: "No Sessions", description: "This certification has no scheduled sessions yet.", status: "warning", duration: 3000, isClosable: true });
            return;
        }
        const certName = courseTitle || "Certification";
        downloadICS(generateMultipleICSContent(allSessions, certName), `${certName.replace(/\s+/g, "_")}_All_Sessions.ics`);
        if (!isMobile) {
            toast({ title: "All Calendar Events Downloaded", description: `${allSessions.length} sessions downloaded. Open the file to add all to your calendar.`, status: "success", duration: 5000, isClosable: true });
        }
    };

    const hasAny = certifications.length > 0 || standaloneCourses.length > 0;

    return (
        <VStack spacing={4} align="stretch">
            {!hasAny && (
                <Box textAlign="center" py={8}>
                    <Text color="gray.500" fontSize="md">No courses enrolled yet.</Text>
                </Box>
            )}

            {standaloneCourses.length > 0 && (
                <Box>
                    <Heading size="sm" mb={4} color="gray.600">
                        Individual Courses
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                        {standaloneCourses.map((course) => {
                            const modStatus = getModuleStatus(course);
                            return (
                                <Card
                                    key={course.id}
                                    p={{ base: 3, md: 4 }}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    borderColor="gray.200"
                                    _hover={{ borderColor: "brand.400", shadow: "md", transform: "translateY(-2px)" }}
                                    transition="all 0.2s"
                                    position="relative"
                                >
                                    <Box
                                        as={Link}
                                        to={`/courses/${course.id}`}
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        right={0}
                                        bottom={0}
                                        zIndex={1}
                                    />
                                    <VStack align="stretch" spacing={{ base: 2, md: 3 }}>
                                        <Box>
                                            <HStack spacing={2} mb={2} flexWrap="wrap">
                                                <Badge colorScheme="green" fontSize={{ base: "xs", md: "sm" }}>
                                                    {course.course_type?.code || course.course_type_code}
                                                </Badge>
                                                {modStatus.label !== "\u2014" && (
                                                    <Badge bg={modStatus.bg} color={modStatus.color} fontSize="xs" textTransform="none">
                                                        {modStatus.label}
                                                    </Badge>
                                                )}
                                            </HStack>
                                            <Heading size={{ base: "xs", md: "sm" }} color="brand.700" fontSize={{ base: "sm", md: "md" }} lineHeight="shorter">
                                                {course.course_type?.name || "Course"}
                                            </Heading>
                                        </Box>
                                        <HStack spacing={2} color="gray.600" flexWrap="wrap" align="center">
                                            <Icon as={FiCalendar} boxSize={{ base: "14px", md: "16px" }} flexShrink={0} />
                                            <Text fontSize={{ base: "xs", md: "sm" }}>
                                                {course.start_date ? moment(course.start_date).format("DD MMM") : "TBC"}
                                                {" - "}
                                                {course.end_date ? moment(course.end_date).format("DD MMM YYYY") : "TBC"}
                                            </Text>
                                        </HStack>
                                        {course.venues?.name && (
                                            <HStack spacing={2} color="gray.600" align="center">
                                                <Icon as={FiMapPin} boxSize={{ base: "14px", md: "16px" }} flexShrink={0} />
                                                <Text fontSize={{ base: "xs", md: "sm" }}>
                                                    {course.venues.name}
                                                    {course.rooms?.name ? ` - ${course.rooms.name}` : ""}
                                                </Text>
                                            </HStack>
                                        )}
                                        {course.trainers?.name && (
                                            <HStack spacing={2} color="gray.600">
                                                <Icon as={FiUser} boxSize={{ base: "14px", md: "16px" }} />
                                                <Text fontSize={{ base: "xs", md: "sm" }}>{course.trainers.name}</Text>
                                            </HStack>
                                        )}
                                        {course.course_dates?.length > 0 && (
                                            <Text fontSize={{ base: "2xs", md: "xs" }} color="green.600" fontWeight="600">
                                                {course.course_dates.length} session{course.course_dates.length !== 1 ? "s" : ""} scheduled
                                            </Text>
                                        )}
                                    </VStack>
                                </Card>
                            );
                        })}
                    </SimpleGrid>
                </Box>
            )}

            {certifications.length > 0 && (
                <Box>
                    {standaloneCourses.length > 0 && (
                        <Heading size="sm" mb={4} color="gray.600">
                            Certification Programs
                        </Heading>
                    )}
                <Accordion allowMultiple>
                    {certifications.map((cert) => {
                        const certModules = modules[cert.id] || [];
                        const certStatus = getCertStatus(cert);

                        return (
                            <AccordionItem key={cert.id} mb={{ base: 3, md: 4 }} border="1px solid" borderColor="gray.200" borderRadius="md">
                                {({ isExpanded }) => (
                                    <>
                                        <AccordionButton
                                            bg="gray.50" _hover={{ bg: "gray.100" }}
                                            p={{ base: 4, md: 6 }}
                                            flexDirection={{ base: "column", md: "row" }}
                                            alignItems={{ base: "flex-start", md: "center" }}
                                        >
                                            <Box flex="1" textAlign="left" w="100%">
                                                <VStack align="flex-start" spacing={2} mb={2}>
                                                    <HStack spacing={2} flexWrap="wrap" align="center">
                                                        <Badge colorScheme="blue" fontSize={{ base: "xs", md: "sm" }}>{cert.run_name}</Badge>
                                                        <Badge bg={certStatus.bg} color={certStatus.color} fontSize={{ base: "xs", md: "sm" }} textTransform="none" fontWeight="600" px={2} py={0.5}>
                                                            {certStatus.label}
                                                        </Badge>
                                                    </HStack>
                                                    <Heading size={{ base: "sm", md: "md" }} color="brand.700" fontSize={{ base: "md", md: "lg" }} width="100%" lineHeight="shorter">
                                                        {cert.full_certification?.name}
                                                    </Heading>
                                                </VStack>
                                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mt={1}>
                                                    {moment(cert.start_date).format("DD MMM YYYY")} – {moment(cert.end_date).format("DD MMM YYYY")}
                                                </Text>
                                            </Box>
                                            <HStack spacing={2} mt={{ base: 2, md: 0 }} onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    size={{ base: "xs", md: "sm" }} colorScheme="purple"
                                                    leftIcon={<Icon as={FaCalendarPlus} />}
                                                    onClick={() => handleDownloadAllCertificationCalendar(cert.id, cert.full_certification?.name || "Certification")}
                                                    w={{ base: "100%", md: "auto" }}
                                                >
                                                    <Text display={{ base: "none", sm: "block" }}>Add All to Calendar</Text>
                                                    <Text display={{ base: "block", sm: "none" }}>Calendar</Text>
                                                </Button>
                                                <AccordionIcon />
                                            </HStack>
                                        </AccordionButton>

                                        <AccordionPanel pb={{ base: 4, md: 6 }} px={{ base: 2, md: 4 }}>
                                            {certModules.length > 0 ? (
                                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                                                    {[...certModules]
                                                        .sort((a, b) => moment(a.start_date).diff(moment(b.start_date)))
                                                        .map((module) => {
                                                            const modStatus = getModuleStatus(module);
                                                            const isOngoing = modStatus.label === 'Ongoing';
                                                            const isCompleted = modStatus.label === 'Completed';
                                                            return (
                                                                <Card
                                                                    key={module.id}
                                                                    p={{ base: 3, md: 4 }} borderWidth="1px" borderRadius="md"
                                                                    bg={isOngoing ? "green.50" : isCompleted ? "gray.50" : "white"}
                                                                    borderColor={isOngoing ? "green.200" : isCompleted ? "gray.300" : "gray.200"}
                                                                    borderLeftWidth={isOngoing ? "4px" : "1px"}
                                                                    borderLeftColor={isOngoing ? "green.400" : isCompleted ? "gray.400" : "gray.200"}
                                                                    opacity={isCompleted ? 0.92 : 1}
                                                                    _hover={
                                                                        isOngoing
                                                                            ? { borderColor: "green.300", bg: "green.100", shadow: "md", transform: "translateY(-2px)" }
                                                                            : isCompleted
                                                                                ? { bg: "gray.100", borderColor: "gray.400", shadow: "sm" }
                                                                                : { borderColor: "brand.400", shadow: "md", transform: "translateY(-2px)" }
                                                                    }
                                                                    transition="all 0.2s" position="relative"
                                                                >
                                                                    <Box
                                                                        as={Link} to={`/courses/${module.id}`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        position="absolute" top={0} left={0} right={0} bottom={0} zIndex={1}
                                                                    />
                                                                    <VStack align="stretch" spacing={{ base: 2, md: 3 }}>
                                                                        <Box>
                                                                            <Badge colorScheme="teal" mb={2} fontSize={{ base: "xs", md: "sm" }}>
                                                                                {module.id || module.course_type?.code}
                                                                            </Badge>
                                                                            <Heading size={{ base: "xs", md: "sm" }} color="brand.700" fontSize={{ base: "sm", md: "md" }} lineHeight="shorter">
                                                                                {module.course_type?.name}
                                                                            </Heading>
                                                                        </Box>

                                                                        <HStack spacing={2} color="gray.600" flexWrap="wrap" align="center">
                                                                            <Icon as={FiCalendar} boxSize={{ base: "14px", md: "16px" }} flexShrink={0} />
                                                                            <Text fontSize={{ base: "xs", md: "sm" }}>
                                                                                {moment(module.start_date).format("DD MMM")} - {moment(module.end_date).format("DD MMM YYYY")}
                                                                            </Text>
                                                                            {modStatus.label !== '\u2014' && (
                                                                                <Badge bg={modStatus.bg} color={modStatus.color} fontSize={{ base: "2xs", md: "xs" }} px={2} py={0.5} fontWeight="600" textTransform="none">
                                                                                    {modStatus.label}
                                                                                </Badge>
                                                                            )}
                                                                        </HStack>

                                                                        {(() => {
                                                                            const venueName = module.venues?.name || (module.course_dates?.length > 0 && module.course_dates[0].venue) || module.venue;
                                                                            const roomName = module.rooms?.name || (module.course_dates?.length > 0 && module.course_dates[0].room);
                                                                            const formatFloorUnit = () => {
                                                                                if (!module.rooms?.floor && !module.rooms?.unit) return "";
                                                                                const parts = [];
                                                                                if (module.rooms.floor) { const f = module.rooms.floor; parts.push(isNaN(f) ? f : `${f}${getOrdinalSuffix(Number(f))} Floor`); }
                                                                                if (module.rooms.unit) parts.push(`Unit ${module.rooms.unit}`);
                                                                                return parts.length > 0 ? ` (${parts.join(", ")})` : "";
                                                                            };
                                                                            if (venueName && venueName !== "TBC" && venueName !== "TBD") {
                                                                                return (
                                                                                    <HStack spacing={2} color="gray.600" align="center" flexWrap="nowrap">
                                                                                        <Icon as={FiMapPin} boxSize={{ base: "14px", md: "16px" }} flexShrink={0} />
                                                                                        <Text fontSize={{ base: "xs", md: "sm" }} flex="1" minW={0}>
                                                                                            {venueName}{roomName && roomName !== "TBC" && roomName !== "TBD" && ` - ${roomName}`}{formatFloorUnit()}
                                                                                        </Text>
                                                                                    </HStack>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })()}

                                                                        {module.trainers && (
                                                                            <HStack spacing={2} color="gray.600" flexWrap="wrap">
                                                                                <Icon as={FiUser} boxSize={{ base: "14px", md: "16px" }} />
                                                                                <Text fontSize={{ base: "xs", md: "sm" }}>{module.trainers.name}</Text>
                                                                            </HStack>
                                                                        )}

                                                                        {module.course_dates?.length > 0 && (
                                                                            <Text fontSize={{ base: "2xs", md: "xs" }} color="purple.600" fontWeight="600">
                                                                                {module.course_dates.length} session{module.course_dates.length !== 1 ? 's' : ''} scheduled
                                                                            </Text>
                                                                        )}
                                                                    </VStack>
                                                                </Card>
                                                            );
                                                        })}
                                                </SimpleGrid>
                                            ) : (
                                                <Box p={4} textAlign="center">
                                                    <Text color="gray.500" fontSize="sm">No modules assigned yet.</Text>
                                                </Box>
                                            )}
                                        </AccordionPanel>
                                    </>
                                )}
                            </AccordionItem>
                        );
                    })}
                </Accordion>
                </Box>
            )}
        </VStack>
    );
};
